import { skipToken } from '@reduxjs/toolkit/query';
import {
  BookOpen,
  Building2,
  ChevronDown,
  ChevronRight,
  FolderKanban,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useTenant } from '@/hooks';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { getMessageByStatus } from '@/lib/error-message';
import {
  useCreateBoardStudentsMutation,
  useCreateBoardTeachersMutation,
  useCreateBoardMutation,
  useDeleteBoardStudentMutation,
  useDeleteBoardTeacherMutation,
  useDeleteBoardMutation,
  useGetBoardDetailQuery,
  useGetBoardStudentsQuery,
  useGetBoardTeachersQuery,
  useGetBoardsQuery,
  useGetBoardsTreeQuery,
  useGetCoursesQuery,
  useGetUsersQuery,
  usePatchBoardStudentMutation,
  usePatchBoardTeacherMutation,
  usePatchBoardMutation,
} from '@/state/course';
import {
  useGetInternalProjectsQuery,
  useAddProjectToBoardMutation,
} from '@/state/project/project-api';
import CreateCourseForm from '@/components/create-course-modal/create-course-form';
import { CreationWizardManager } from '@/components/projects/creation-wizard/manager';
import { useGetAllClientUserListQuery, useGetCurrentUserQuery } from '@/state';
import type {
  Board,
  BoardTeacher,
  BoardTreeNode,
  CreateBoardPayload,
  EnrolledStudent,
} from '@/types/api/course.types';
import type { ApiErrorResponse } from '@/types/async-boundary.types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import {
  AddBoardUsersDrawer,
  BoardPeopleAccessSection,
  type BoardPersonItem,
  BoardFormDialog,
  BoardsBreadcrumb,
  BoardsCardsSection,
  BoardsCoursesSection,
  BoardsProjectsSection,
  BoardsDetailPanel,
  BoardsEmptyState,
  BoardsHeader,
  BoardsHierarchySection,
} from './components/boards-page-sections';

type BoardFormState = {
  board_name: string;
  description: string;
  head_of_board: string;
  parent: string;
};
type BoardEditorItem = Pick<
  Board,
  'id' | 'board_name' | 'description' | 'head_of_board' | 'parent'
>;

const DEFAULT_FORM: BoardFormState = {
  board_name: '',
  description: '',
  head_of_board: '',
  parent: 'none',
};

type BoardViewMode = 'cards' | 'hierarchy';
type DetailTab = 'overview' | 'courses' | 'projects' | 'people-access';
type BoardsLocationState = {
  trail?: BoardTreeNode[];
};

const BoardsManagement = () => {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const { '*': boardPathParam } = useParams();
  const { isSchoolAdmin, isTeacher, isStudent } = useClientPermissions();
  const canAccess = isSchoolAdmin || isTeacher || isStudent;
  const canManage = isSchoolAdmin || isTeacher;
  const isMyBoardsRoute = location.pathname.includes('/my-boards');
  const boardsBasePath = isMyBoardsRoute ? 'my-boards' : 'admin-tools/boards';

  const [open, setOpen] = useState(false);
  const [editingBoard, setEditingBoard] = useState<BoardEditorItem | null>(null);
  const [form, setForm] = useState<BoardFormState>(DEFAULT_FORM);
  const [viewMode, setViewMode] = useState<BoardViewMode>('cards');
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  const [expandedTreeNodes, setExpandedTreeNodes] = useState<Record<number, boolean>>({});
  const [addUsersOpen, setAddUsersOpen] = useState(false);
  const [usersSearch, setUsersSearch] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [personToRemove, setPersonToRemove] = useState<BoardPersonItem | null>(null);
  const [createCourseOpen, setCreateCourseOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);

  const boardPathIds = useMemo(
    () =>
      (boardPathParam || '')
        .split('/')
        .filter(Boolean)
        .map((idText) => Number(idText))
        .filter((idNum) => !Number.isNaN(idNum)),
    [boardPathParam]
  );
  const currentBoardId = boardPathIds.length ? boardPathIds[boardPathIds.length - 1] : undefined;

  const tenantArg = tenant ? { tenant } : skipToken;
  const { data: currentUser } = useGetCurrentUserQuery(
    tenant ? { tenant } : skipToken
  );
  const currentUserId = currentUser?.id;
  const buildRoleScopedFilters = () => {
    if (isTeacher && currentUserId) {
      return {
        teacher_board_teacher: currentUserId,
        board_enroll_student: '',
      };
    }
    if (isStudent && currentUserId) {
      return {
        teacher_board_teacher: '',
        board_enroll_student: currentUserId,
      };
    }
    return {
      teacher_board_teacher: '',
      board_enroll_student: '',
    };
  };
  const roleScopedFilters = buildRoleScopedFilters();
  const boardsArg = tenant
    ? {
        tenant,
        level: 0,
        ...roleScopedFilters,
      }
    : skipToken;
  const allBoardsArg = tenant
    ? {
        tenant,
        ...roleScopedFilters,
      }
    : skipToken;
  const {
    data: boards = [],
    isLoading: isRootBoardsLoading,
    error: rootBoardsError,
  } = useGetBoardsQuery(boardsArg);
  const { data: allBoards = [] } = useGetBoardsQuery(allBoardsArg);
  const { data: users = [] } = useGetUsersQuery(tenantArg);
  const { data: allClientUsers = [] } = useGetAllClientUserListQuery(tenantArg);
  const buildRoleScopedTreeArgs = (
    base: { tenant: string; level: number; parent?: number }
  ) => {
    return {
      ...base,
      ...roleScopedFilters,
    };
  };
  const locationState = (location.state as BoardsLocationState | null) ?? null;
  const breadcrumbTrailFromState = locationState?.trail ?? [];
  const shouldFetchSubBoards =
    viewMode === 'cards' && detailTab === 'overview' && !!currentBoardId;
  const subBoardsArg =
    tenant && currentBoardId && shouldFetchSubBoards
      ? {
          tenant,
          parent: currentBoardId,
          ...roleScopedFilters,
        }
      : skipToken;
  const {
    data: subBoards = [],
    isLoading: isSubBoardsLoading,
    error: subBoardsError,
  } = useGetBoardsQuery(subBoardsArg);
  const boardCoursesArg =
    tenant && currentBoardId && viewMode === 'cards' && detailTab === 'courses'
      ? { tenant, board: currentBoardId }
      : skipToken;
  const {
    data: boardCourses = [],
    isLoading: isBoardCoursesLoading,
    error: boardCoursesError,
  } = useGetCoursesQuery(boardCoursesArg);
  const boardProjectsArg =
    tenant && currentBoardId && viewMode === 'cards' && detailTab === 'projects'
      ? { tenant, board: currentBoardId }
      : skipToken;
  const {
    data: boardProjects = [],
    isLoading: isBoardProjectsLoading,
    error: boardProjectsError,
  } = useGetInternalProjectsQuery(boardProjectsArg);
  const rootTreeArgs = tenant
    ? buildRoleScopedTreeArgs({ tenant, level: 0 })
    : null;
  const {
    data: rootBoardsTree = [],
    isLoading: isRootTreeLoading,
    error: rootTreeError,
  } = useGetBoardsTreeQuery(rootTreeArgs ?? skipToken);
  const findNodeById = (nodes: BoardTreeNode[], nodeId: number): BoardTreeNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      const childFound = findNodeById(node.children ?? [], nodeId);
      if (childFound) return childFound;
    }
    return null;
  };
  const derivedTrail = useMemo(() => {
    const findInTree = (nodes: BoardTreeNode[], nodeId: number): BoardTreeNode | null => {
      for (const node of nodes) {
        if (node.id === nodeId) return node;
        const childFound = findInTree(node.children ?? [], nodeId);
        if (childFound) return childFound;
      }
      return null;
    };
    if (!boardPathIds.length) return [];
    const trail: BoardTreeNode[] = [];
    const roots = rootBoardsTree;
    for (const id of boardPathIds) {
      const found = findInTree(roots, id);
      if (found) trail.push(found);
    }
    return trail;
  }, [boardPathIds, rootBoardsTree]);
  const breadcrumbTrail =
    breadcrumbTrailFromState.length >= derivedTrail.length
      ? breadcrumbTrailFromState
      : derivedTrail;
  const selectedBoard =
    (currentBoardId
      ? [...breadcrumbTrail].reverse().find((node) => node.id === currentBoardId) || null
      : null);
  const selectedNodeFromRootTree = currentBoardId
    ? findNodeById(rootBoardsTree, currentBoardId)
    : null;
  const boardDetailArgs =
    tenant && currentBoardId ? { tenant, boardId: currentBoardId } : skipToken;
  const { data: boardDetail } = useGetBoardDetailQuery(boardDetailArgs);
  const boardPeopleArgs =
    tenant && currentBoardId ? { tenant, boardId: currentBoardId } : skipToken;
  const { data: boardTeachers = [] } = useGetBoardTeachersQuery(boardPeopleArgs);
  const { data: boardStudents = [] } = useGetBoardStudentsQuery(boardPeopleArgs);

  const [createBoard, { isLoading: isCreating }] = useCreateBoardMutation();
  const [patchBoard, { isLoading: isUpdating }] = usePatchBoardMutation();
  const [deleteBoard, { isLoading: isDeleting }] = useDeleteBoardMutation();
  const [createBoardTeachers, { isLoading: isCreatingBoardTeachers }] =
    useCreateBoardTeachersMutation();
  const [createBoardStudents, { isLoading: isCreatingBoardStudents }] =
    useCreateBoardStudentsMutation();
  const [patchBoardTeacher, { isLoading: isPatchingBoardTeacher }] =
    usePatchBoardTeacherMutation();
  const [patchBoardStudent, { isLoading: isPatchingBoardStudent }] =
    usePatchBoardStudentMutation();
  const [deleteBoardTeacher, { isLoading: isDeletingBoardTeacher }] =
    useDeleteBoardTeacherMutation();
  const [deleteBoardStudent, { isLoading: isDeletingBoardStudent }] =
    useDeleteBoardStudentMutation();
  const [addProjectToBoard] = useAddProjectToBoardMutation();

  const handleProjectCreated = async (projectId: number) => {
    if (!tenant || !currentBoardId) return;
    try {
      await addProjectToBoard({ tenant, projectId, boardId: currentBoardId }).unwrap();
      toast.success('Project created and added to board');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Project created, but could not be added to board', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  const parentOptions = useMemo(
    () => allBoards.filter((board) => board.id !== editingBoard?.id),
    [allBoards, editingBoard?.id]
  );

  const isSubmitting = isCreating || isUpdating;
  const isSavingPeople = isCreatingBoardTeachers || isCreatingBoardStudents;
  const isMarkingInactive = isPatchingBoardTeacher || isPatchingBoardStudent;
  const isRemovingPeople = isDeletingBoardTeacher || isDeletingBoardStudent;

  const resetForm = () => {
    setEditingBoard(null);
    setForm({
      ...DEFAULT_FORM,
      head_of_board: currentUserId ?? '',
    });
  };

  const openCreate = () => {
    resetForm();
    setForm((prev) => ({
      ...prev,
      parent: currentBoardId ? String(currentBoardId) : 'none',
    }));
    setOpen(true);
  };

  const openEdit = (board: BoardEditorItem) => {
    setEditingBoard(board);
    setForm({
      board_name: board.board_name,
      description: board.description ?? '',
      head_of_board: board.head_of_board || currentUserId || '',
      parent: board.parent ? String(board.parent) : 'none',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!tenant) return;

    const trimmedName = form.board_name.trim();
    const trimmedDescription = form.description.trim();
    if (!trimmedName) {
      toast.error('Board name is required');
      return;
    }
    if (!trimmedDescription) {
      toast.error('Description is required');
      return;
    }

    const payload: CreateBoardPayload = {
      board_name: trimmedName,
      description: trimmedDescription,
      head_of_board: form.head_of_board || currentUserId || null,
      parent: form.parent === 'none' ? null : Number(form.parent),
    };

    try {
      if (editingBoard) {
        await patchBoard({
          tenant,
          boardId: editingBoard.id,
          data: payload,
        }).unwrap();
        toast.success('Board updated');
      } else {
        await createBoard({ tenant, data: payload }).unwrap();
        toast.success('Board created');
      }
      setOpen(false);
      resetForm();
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Unable to save board', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  const handleDelete = async (board: Pick<Board, 'id' | 'board_name'>) => {
    if (!tenant || isDeleting) return;

    const confirmed = window.confirm(
      `Delete "${board.board_name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      await deleteBoard({ tenant, boardId: board.id }).unwrap();
      toast.success('Board deleted');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Unable to delete board', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  const peopleList = useMemo<BoardPersonItem[]>(() => {
    const list: BoardPersonItem[] = [];
    if (boardDetail?.board_head?.head_of_board) {
      list.push({
        key: `head-${boardDetail.board_head.head_of_board}`,
        personId: boardDetail.board_head.head_of_board,
        firstName: boardDetail.board_head.first_name,
        lastName: boardDetail.board_head.last_name,
        email: boardDetail.board_head.email,
        photoUrl: boardDetail.board_head.photo_url,
        role: 'Head of board',
        type: 'head',
        active: true,
      });
    }
    boardTeachers.forEach((teacherMap: BoardTeacher) => {
      list.push({
        key: `teacher-${teacherMap.id}`,
        personId: teacherMap.teacher.id,
        firstName: teacherMap.teacher.first_name,
        lastName: teacherMap.teacher.last_name,
        email: teacherMap.teacher.email,
        photoUrl: teacherMap.teacher.photo_url,
        role: 'Instructor',
        type: 'teacher',
        active: teacherMap.active,
        mappingId: teacherMap.id,
      });
    });
    boardStudents.forEach((studentMap: EnrolledStudent) => {
      list.push({
        key: `student-${studentMap.id}`,
        personId: studentMap.student.id,
        firstName: studentMap.student.first_name,
        lastName: studentMap.student.last_name,
        email: studentMap.student.email,
        photoUrl: studentMap.student.photo_url,
        role: 'Learner',
        type: 'student',
        active: studentMap.active,
        mappingId: studentMap.id,
      });
    });
    return list;
  }, [boardDetail, boardTeachers, boardStudents]);

  const existingBoardUserIds = useMemo(() => {
    return new Set(peopleList.map((person) => person.personId));
  }, [peopleList]);

  const availableUsersForBoard = useMemo(
    () =>
      allClientUsers.filter(
        (user) =>
          !existingBoardUserIds.has(user.id) &&
          user.id !== boardDetail?.board_head?.head_of_board
      ),
    [allClientUsers, existingBoardUserIds, boardDetail?.board_head?.head_of_board]
  );

  const toggleSelectedUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const resolveUserType = (userId: string): 'teacher' | 'student' => {
    const found = allClientUsers.find((user) => user.id === userId);
    const normalizedRoles = (found?.user_in_client_roles || []).map((role) =>
      role.trim().toUpperCase().replace(/\s+/g, '_')
    );
    if (normalizedRoles.includes('STUDENT')) return 'student';
    return 'teacher';
  };

  const handleSaveSelectedUsers = async () => {
    if (!tenant || !currentBoardId || !selectedUserIds.length) return;
    const teacherIds = selectedUserIds.filter((id) => resolveUserType(id) === 'teacher');
    const studentIds = selectedUserIds.filter((id) => resolveUserType(id) === 'student');

    try {
      if (teacherIds.length) {
        await createBoardTeachers({
          tenant,
          data: {
            teachers: teacherIds.map((id) => ({
              teacher: id,
              board: currentBoardId,
              active: true,
            })),
          },
        }).unwrap();
      }

      if (studentIds.length) {
        await createBoardStudents({
          tenant,
          data: {
            students: studentIds.map((id) => ({
              student: id,
              board: currentBoardId,
              semester_course: null,
              active: true,
            })),
          },
        }).unwrap();
      }
      toast.success('Users added to board');
      setSelectedUserIds([]);
      setUsersSearch('');
      setAddUsersOpen(false);
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Failed to add users', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  const handleMarkInactive = async (person: BoardPersonItem) => {
    if (!tenant || !currentBoardId || !person.mappingId) return;

    try {
      if (person.type === 'teacher') {
        await patchBoardTeacher({
          tenant,
          boardTeacherId: person.mappingId,
          data: { active: false, board: currentBoardId },
        }).unwrap();
      } else if (person.type === 'student') {
        await patchBoardStudent({
          tenant,
          enrollmentId: person.mappingId,
          data: { active: false, board: currentBoardId },
        }).unwrap();
      }
      toast.success('User marked as inactive');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Failed to update user', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    }
  };

  const handleRemovePerson = async (person: BoardPersonItem) => {
    if (!tenant || !person.mappingId) return;

    try {
      if (person.type === 'teacher') {
        await deleteBoardTeacher({
          tenant,
          boardTeacherId: person.mappingId,
        }).unwrap();
      } else if (person.type === 'student') {
        await deleteBoardStudent({
          tenant,
          enrollmentId: person.mappingId,
        }).unwrap();
      }
      toast.success('User removed from board');
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      toast.error('Failed to remove user', {
        description: getMessageByStatus(apiError?.status || 500),
      });
    } finally {
      setPersonToRemove(null);
    }
  };

  const toggleTreeNode = (nodeId: number) => {
    setExpandedTreeNodes((prev) => ({
      ...prev,
      [nodeId]: !(prev[nodeId] ?? true),
    }));
  };

  const handleOpenBoard = (node: BoardTreeNode) => {
    const nextPath = `${location.pathname.replace(/\/$/, '')}/${node.id}`;
    const nextTrail = [...breadcrumbTrail, node];
    navigate(nextPath, { state: { trail: nextTrail } });
    setDetailTab('overview');
  };

  const openRootView = () => {
    navigate(`/${tenant || ''}/${boardsBasePath}`);
    setDetailTab('overview');
  };

  const jumpToBreadcrumb = (index: number) => {
    const nextTrail = breadcrumbTrail.slice(0, index + 1);
    const targetPath = `/${tenant}/${boardsBasePath}/${nextTrail.map((item) => item.id).join('/')}`;
    navigate(targetPath, { state: { trail: nextTrail } });
    setDetailTab('overview');
  };

  const visibleNodes = currentBoardId ? subBoards : boards;
  const treeNodesForHierarchy = currentBoardId && selectedNodeFromRootTree
    ? [selectedNodeFromRootTree]
    : rootBoardsTree;
  const isTreeLoading = isRootTreeLoading;
  const treeError = rootTreeError;
  const isCardsLoading = currentBoardId ? isSubBoardsLoading : isRootBoardsLoading;
  const cardsError = currentBoardId ? subBoardsError : rootBoardsError;

  const renderTreeNodes = (nodes: BoardTreeNode[], depth = 0): ReactNode => {
    return nodes.map((node) => {
      const hasChildren = (node.children?.length ?? 0) > 0;
      const isExpanded = expandedTreeNodes[node.id] ?? true;

      return (
        <div
          key={node.id}
          className={depth > 0 ? 'ml-6 border-l border-border dark:border-white/10' : ''}
        >
          <div
            className="group flex items-center gap-2 rounded-xl px-3 py-2 text-foreground hover:bg-muted/60 dark:text-slate-100 dark:hover:bg-white/5"
            style={{ marginLeft: `${depth > 0 ? 8 : 0}px` }}
          >
            {hasChildren ? (
              <button
                type="button"
                className="rounded p-0.5 hover:bg-muted dark:hover:bg-white/10"
                onClick={() => toggleTreeNode(node.id)}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            ) : (
              <span className="h-5 w-5" />
            )}
            <Building2 className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[22px] leading-tight font-medium sm:text-[28px]">
                {node.board_name}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground dark:bg-slate-800/80 dark:text-slate-200">
                Board
              </span>
              {hasChildren && (
                <span className="text-xs text-muted-foreground">{node.children.length} sub</span>
              )}
            </div>
          </div>

          {hasChildren && isExpanded ? renderTreeNodes(node.children, depth + 1) : null}
        </div>
      );
    });
  };

  if (!canAccess) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        You do not have permission to view boards.
      </div>
    );
  }

  if (isCardsLoading && !currentBoardId) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (cardsError) {
    return (
      <div className="p-8 text-center text-red-500">
        Error loading boards. Please try again later.
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto w-full max-w-[1700px] space-y-6 px-4 py-6 text-foreground md:px-6">
        <BoardsHeader
          currentBoardId={currentBoardId}
          selectedBoardName={selectedBoard?.board_name}
          canManage={canManage}
          viewMode={viewMode}
          onBack={() => navigate(-1)}
          onOpenCreate={openCreate}
          onViewChange={setViewMode}
        />

        <BoardsBreadcrumb
          breadcrumbTrail={breadcrumbTrail}
          onRootClick={openRootView}
          onJump={jumpToBreadcrumb}
        />

        {!!currentBoardId && selectedBoard && viewMode === 'cards' ? (
          <BoardsDetailPanel
            selectedBoard={selectedBoard}
            detailTab={detailTab}
            onTabChange={setDetailTab}
          />
        ) : null}

        {viewMode === 'cards' && detailTab === 'people-access' && !!currentBoardId ? (
          <BoardPeopleAccessSection
            people={peopleList}
            canManage={canManage}
            onOpenAddUsers={() => setAddUsersOpen(true)}
            onMarkInactive={handleMarkInactive}
            onRemove={setPersonToRemove}
            isMarkingInactive={isMarkingInactive}
            isRemoving={isRemovingPeople}
          />
        ) : viewMode === 'cards' && detailTab === 'courses' && !!currentBoardId ? (
          isBoardCoursesLoading ? (
            <div className="rounded-xl border border-border p-8 text-center text-muted-foreground dark:border-white/10">
              Loading courses...
            </div>
          ) : boardCoursesError ? (
            <div className="rounded-xl border border-border p-8 text-center text-red-500 dark:border-white/10">
              Unable to load courses.
            </div>
          ) : boardCourses.length === 0 ? (
            <BoardsEmptyState
              title="Courses"
              heading="No courses found"
              description="There are no courses under this board yet."
              sectionIcon={
                <BookOpen className="h-6 w-6 text-muted-foreground dark:text-slate-300" />
              }
              icon={
                <BookOpen className="h-7 w-7 text-muted-foreground dark:text-slate-400" />
              }
              showCreateAction={canManage}
              createActionLabel="Add course"
              onOpenCreate={() => setCreateCourseOpen(true)}
            />
          ) : (
            <BoardsCoursesSection
              courses={boardCourses}
              onOpenCourse={(courseId) => navigate(`/${tenant}/courses/${courseId}`)}
              onOpenCreate={() => setCreateCourseOpen(true)}
              canManage={canManage}
            />
          )
        ) : viewMode === 'cards' && detailTab === 'projects' && !!currentBoardId ? (
          isBoardProjectsLoading ? (
            <div className="rounded-xl border border-border p-8 text-center text-muted-foreground">Loading projects...</div>
          ) : boardProjectsError ? (
            <div className="rounded-xl border border-border p-8 text-center text-red-500">Unable to load projects.</div>
          ) : boardProjects.length === 0 ? (
            <BoardsEmptyState
              title="Projects"
              heading="No projects found"
              description="There are no projects under this board yet."
              sectionIcon={<FolderKanban className="h-6 w-6 text-muted-foreground dark:text-slate-300" />}
              icon={<FolderKanban className="h-7 w-7 text-muted-foreground dark:text-slate-400" />}
              showCreateAction={canManage}
              createActionLabel="Add project"
              onOpenCreate={() => setCreateProjectOpen(true)}
            />
          ) : (
            <BoardsProjectsSection
              projects={boardProjects}
              onOpenProject={(projectId) => navigate(`/${tenant}/projects/${projectId}`)}
              onOpenCreate={() => setCreateProjectOpen(true)}
              canManage={canManage}
            />
          )
        ) : viewMode === 'cards' && isCardsLoading ? (
          <div className="rounded-xl border border-border p-8 text-center text-muted-foreground dark:border-white/10">
            Loading boards...
          </div>
        ) : viewMode === 'cards' && visibleNodes.length === 0 ? (
          <BoardsEmptyState
            title={currentBoardId ? 'Sub-Boards' : 'Faculties & Departments'}
            heading={currentBoardId ? 'No sub-boards found' : 'No boards found'}
            description={
              currentBoardId
                ? 'This is the lowest level in this hierarchy.'
                : 'Create your first board to get started.'
            }
            showCreateAction={!!currentBoardId && canManage}
            createActionLabel="Create new Sub-board"
            onOpenCreate={openCreate}
          />
        ) : viewMode === 'cards' ? (
          <BoardsCardsSection
            currentBoardId={currentBoardId}
            visibleNodes={visibleNodes}
            rootBoardsTree={rootBoardsTree}
            canManage={canManage}
            isDeleting={isDeleting}
            onOpenBoard={handleOpenBoard}
            onOpenEdit={openEdit}
            onDelete={handleDelete}
          />
        ) : (
          <BoardsHierarchySection
            isTreeLoading={isTreeLoading}
            treeError={treeError}
            treeNodesForHierarchy={treeNodesForHierarchy}
            renderTreeNodes={renderTreeNodes}
          />
        )}
      </div>

      {canManage && (
        <BoardFormDialog
          open={open}
          setOpen={setOpen}
          editingBoard={editingBoard}
          form={form}
          setForm={setForm}
          users={users}
          parentOptions={parentOptions}
          onCancel={() => {
            setOpen(false);
            resetForm();
          }}
          onSave={handleSave}
          isSubmitting={isSubmitting}
        />
      )}
      {canManage && !!currentBoardId && (
        <AddBoardUsersDrawer
          open={addUsersOpen}
          onOpenChange={(next) => {
            setAddUsersOpen(next);
            if (!next) {
              setSelectedUserIds([]);
              setUsersSearch('');
            }
          }}
          users={availableUsersForBoard}
          selectedIds={selectedUserIds}
          onToggleUser={toggleSelectedUser}
          search={usersSearch}
          onSearchChange={setUsersSearch}
          onSave={handleSaveSelectedUsers}
          isSaving={isSavingPeople}
        />
      )}
      {canManage && !!currentBoardId && (
        <CreateCourseForm
          isOpen={createCourseOpen}
          onClose={() => setCreateCourseOpen(false)}
          defaultBoardId={currentBoardId}
        />
      )}
      {canManage && !!currentBoardId && (
        <CreationWizardManager
          open={createProjectOpen}
          onOpenChange={setCreateProjectOpen}
          projectType="INTERNAL"
          onCreated={handleProjectCreated}
        />
      )}
      <AlertDialog
        open={!!personToRemove}
        onOpenChange={(open) => {
          if (!open) setPersonToRemove(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user?</AlertDialogTitle>
            <AlertDialogDescription>
              {personToRemove
                ? `This will remove ${personToRemove.firstName} ${personToRemove.lastName} from this board.`
                : 'This will remove the user from this board.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => {
                if (personToRemove) {
                  void handleRemovePerson(personToRemove);
                }
              }}
              disabled={isRemovingPeople}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BoardsManagement;
